import { Link, useLocation } from "react-router-dom";
import { Phone, Mail, MessageCircle } from "lucide-react";
import { CONTACT, NAV_LINKS, mailto } from "@/data/content";
import { FadeUp } from "@/components/Reveal";
import { trackMicroConversion } from "@/lib/tracking";

const Footer = () => {
  const { pathname } = useLocation();
  const isLanding = pathname === "/freie-trauung-krefeld";

  const ctaClick = (e) => {
    if (!isLanding) return;
    e.preventDefault();
    trackMicroConversion("cta");
    document.getElementById("anfrage")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer data-testid="site-footer" className="bg-mossdeep text-creme">
      <div className="mx-auto max-w-7xl px-5 pb-10 pt-20 sm:px-8 sm:pt-28">
        <FadeUp>
          <div className="flex flex-col gap-10 border-b border-creme/15 pb-14 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-sand">Lassen Sie uns sprechen</p>
              <p className="mt-4 font-serif text-5xl leading-[1.05] sm:text-7xl">
                Ihr Moment<br />
                <span className="font-accent italic text-sand">verdient Worte.</span>
              </p>
            </div>
            <a
              href={isLanding ? "#anfrage" : mailto("Anfrage Zeremonie")}
              onClick={ctaClick}
              data-testid="footer-cta-button"
              className="inline-flex w-fit items-center gap-3 rounded-full bg-creme px-8 py-4 text-sm font-bold text-mossdeep transition-transform duration-300 hover:scale-[1.03]"
            >
              Unverbindlich Kontakt aufnehmen
            </a>
          </div>
        </FadeUp>

        <div className="grid gap-12 py-14 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <p className="font-serif text-2xl">Daviz <span className="font-accent italic text-sand">&</span> Puxi</p>
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-creme/70">
              Persönliche Zeremonien für besondere Momente im Leben – individuell, authentisch und mit Menschen gestaltet, die zuhören.
            </p>
            <div className="mt-6 flex gap-3">
              <a
                href={CONTACT.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-social-whatsapp"
                aria-label="WhatsApp"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-creme/20 text-creme/80 transition-colors hover:border-sand hover:text-sand"
              >
                <MessageCircle size={16} />
              </a>
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sand">Mark Daviz</p>
            <p className="mt-4 text-sm text-creme/70">Freier Redner</p>
            <Link to="/mark-daviz" data-testid="footer-link-mark" className="link-underline mt-4 inline-block text-sm text-creme">Über Mark</Link>
            <p className="mt-8 text-xs font-semibold uppercase tracking-[0.25em] text-sand">Angela Puxi</p>
            <p className="mt-4 text-sm text-creme/70">Freie Rednerin</p>
            <Link to="/angela-puxi" data-testid="footer-link-angela" className="link-underline mt-4 inline-block text-sm text-creme">Über Angela</Link>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sand">Navigation</p>
            <ul className="mt-4 space-y-2.5">
              <li><Link to="/" data-testid="footer-nav-home" className="text-sm text-creme/75 transition-colors hover:text-creme">Home</Link></li>
              <li><Link to="/freie-trauung-krefeld" data-testid="footer-nav-freie-trauung-krefeld" className="text-sm text-creme/75 transition-colors hover:text-creme">Freie Trauung Krefeld</Link></li>
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} data-testid={`footer-nav-${l.to.slice(1)}`} className="text-sm text-creme/75 transition-colors hover:text-creme">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sand">Kontakt</p>
            <div className="mt-4 space-y-3 text-sm">
              <a
                href={CONTACT.phoneHref}
                data-testid="footer-phone"
                onClick={() => trackMicroConversion("phone")}
                className="flex items-center gap-3 text-creme/75 transition-colors hover:text-creme"
              >
                <Phone size={15} className="text-sand" /> {CONTACT.phone}
              </a>
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="footer-email"
                onClick={() => trackMicroConversion("email")}
                className="flex items-center gap-3 text-creme/75 transition-colors hover:text-creme"
              >
                <Mail size={15} className="text-sand" /> {CONTACT.email}
              </a>
              <p className="pt-2 text-creme/50">Deutschlandweit auf Anfrage</p>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-start justify-between gap-4 border-t border-creme/15 pt-8 text-xs text-creme/50 sm:flex-row sm:items-center">
          <p>© {new Date().getFullYear()} Mark Daviz & Angela Puxi. Alle Rechte vorbehalten.</p>
          <div className="flex gap-6">
            <Link to="/impressum" data-testid="footer-link-impressum" className="transition-colors hover:text-creme">Impressum</Link>
            <Link to="/datenschutz" data-testid="footer-link-datenschutz" className="transition-colors hover:text-creme">Datenschutz</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
